import { ReactNode } from 'react';

import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { ResourceState } from 'ia-react-core/~/interfaces/ResourceState';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';

export interface ResourceAliasProps<Data, Error = {}> {
  /** Path to the resource to be aliased. */
  resourceSrc?: string;
  /** Callback called with the axios request config. Must return the final config of the request. */
  onRequest?: (config: AxiosRequestConfig) => void;
  /** Callback called with the response when the request is successful. */
  onSuccess?: (data: AxiosResponse<Data>) => void;
  /** Callback called with the error when the request is not successful. */
  onFailure?: (error: Error) => void;
  /** Callback when the request ends, either in success or failure. */
  onComplete?: () => void;
  /** Callback when the request is cancelled. */
  onCancel?: () => void;
}

interface ResourceAliasInternalsProps {
  resourceAliasId: number;
  scope: string[];
  store: AugmentedStore<ResourceState>;
}

export interface ResourceAliasComposedProps<Data, Error = {}> extends ResourceAliasProps<Data, Error>, ResourceAliasInternalsProps {}

export interface ResourceAliasStatics {
  type: Symbol;
  defaultProps?: Partial<ResourceAliasComposedProps<{}>>;
  publicProps: (StringKeyOf<ResourceAliasProps<{}>>)[];
}

const ResourceAliasDefaultProps = {};

const ResourceAlias: (<Data, Error = {}>(
  props: ResourceAliasComposedProps<Data, Error>
) => ReactNode) & ResourceAliasStatics = () => null;

ResourceAlias.type = Symbol('ResourceAlias');
ResourceAlias.defaultProps = ResourceAliasDefaultProps;
ResourceAlias.publicProps = ['resourceSrc', 'onRequest', 'onSuccess', 'onFailure', 'onComplete'];

/**
 * @visibleName ResourceAlias
 * @component
 */
export default ResourceAlias;
